/**
 * getWidths and generateImgAttrs are utility functions taken from the next/Image library
 *
 * getWidths function helps accurately define the correct width based
 * on sizes attr, viewport size and device pixel ratio
 *
 * generateImgAttrs function takes in a custom config and loader to create the appropriate
 * src and srcset attributes of the image to fetch
 */
type GenImgAttrsData = {
  src: string
  loader: (options: Omit<GenImgAttrsData, 'loader'>) => string
  width?: number
  quality?: number
  sizes?: string
}

type GenImgAttrsResult = {
  src: string
  srcSet: string | undefined
  sizes: string | undefined
}

export const getWidths = (width = 1600): number[] => {
  const widths: number[] = [width]
  for (let w = Math.floor(width / 2); w > 200; w = Math.floor(w / 2)) {
    widths.push(w)
  }
  return widths.reverse()
}

export const generateImgAttrs = ({ src, width, quality, sizes, loader }: GenImgAttrsData): GenImgAttrsResult => {
  const widths = getWidths(width)

  return {
    sizes,
    srcSet: widths.map((w) => `${loader({ src, quality, width: w })} ${w}w`).join(', '),

    // It's intended to keep `src` the last attribute because React updates
    // attributes in order. If we keep `src` the first one, Safari will
    // immediately start to fetch `src`, before `sizes` and `srcSet` are even
    // updated by React. That causes multiple unnecessary requests if `srcSet`
    // and `sizes` are defined.
    // This bug cannot be reproduced in Chrome or Firefox.
    src: loader({ src, quality, width }),
  }
}
